<template>
    <template v-if="loaded">
        <ReviewMode v-if="review && !comingSoon">
            <component :is="currentComponent" v-bind="currentProps" />
        </ReviewMode>
        <component v-else :is="currentComponent" v-bind="currentProps" />
    </template>
    <Cursor />
</template>

<script>
import PortfolioPage from "./Portfolio.vue";
import ApiHelper from "../../api/ApiHelper.js";
import {checkFonts} from "../../settingsEditor/_helpers/fontLoaders.js";
import PasswordProtection from "./common/PasswordProtection.vue";
import ErrorPage from "../common/ErrorPage.vue";
import GoogleFontsHelper from "../../common/GoogleFontsHelper.js";
import ReviewMode from "../../view/review/ReviewMode.vue";
import ComingSoon from "../../common/ComingSoon.vue";
import {isOwnerPreview} from "./ownerPreview.js";
import Cursor from "../../common/sideBars/design/Cursor.vue";
import {mapGetters} from "vuex";
import {UserActions} from "@/scripts/store/actions/UserActions.js";
import {PortfolioActions} from "@/scripts/store/actions/PortfolioActions.js";
import {store} from "@/scripts/store/FolioStore.js";
import {
    isGeneratedPortfolioTemplatePreview,
    isTemplatePreviewRoute,
    readGeneratedPortfolioPreview
} from "@/scripts/adminSite/aiPortfolioGenerator/portfolioPreviewCache.js";
import {getPortfolioGenerationPreview} from "@/scripts/api/PortfolioGenerationAPI.js";
import {
    findPortfolioGenerationPreviewPage,
    isPortfolioGenerationPreviewRoute
} from "@/scripts/view/portfolio/generationPreview.js";

export default {
    name: "PortfolioWrapper",
    components: {
        ReviewMode,
        PasswordProtection,
        PortfolioPage,
        ErrorPage,
        ComingSoon,
        Cursor
    },
    //These coming from the IndexWrapper, as we actualy getting the data for portfolio in index view, for quicker render
    props:['ownDomainError', 'ownDomainPassword', 'ownDomainLoaded', 'ownDomainComingSoon', 'review'],
    data: () => ({
        password: false,
        error: false,
        comingSoon: false,
        loaded:false
    }),
    mounted() {
        this.comingSoon = !!this.ownDomainComingSoon;
        this.error = !!this.ownDomainError;
        this.password = !!this.ownDomainPassword;
        if(this.ownDomainLoaded) {
            this.loaded = true;
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.loadPortfolioIfNeeded(to));
    },
    beforeRouteUpdate(to, from, next) {
        this.loadPortfolioIfNeeded(to);
        next();
    },
    methods: {
        async loadPortfolioIfNeeded(to) {
            if (isPortfolioGenerationPreviewRoute(to)) {
                await this.loadPortfolioGenerationPreview(to);
                return;
            }

            if (this.ownDomainLoaded || this.hydrateFromExistingViewData(to)) {
                return;
            }

            if (this.isPreviewingPortfolio(to)) {
                return;
            }

            if (this.hydrateFromGeneratedPreviewCache(to)) {
                return;
            }

            try {
                const url = this.fetchUrl(to);
                const res = await ApiHelper.get(url);
                this.setData(null, res.data);
            } catch (err) {
                this.setData(err, null);
            }
        },
        async loadPortfolioGenerationPreview(to) {
            const generationId = to.params.generationId;
            if (this.hydrateFromExistingGenerationPreview(generationId)) {
                return;
            }

            try {
                const res = await getPortfolioGenerationPreview(generationId);
                const payload = res.data;
                const homePage = payload.project
                    || findPortfolioGenerationPreviewPage(payload, "home");
                if (!homePage) {
                    throw new Error("Portfolio preview has no home page.");
                }

                this.setData(null, {...payload, project: homePage}, generationId);
            } catch (error) {
                this.setPreviewError();
            }
        },
        setPreviewError() {
            this.error = true;
            this.password = false;
            this.comingSoon = false;
            this.loaded = true;
        },
        hydrateFromExistingGenerationPreview(generationId) {
            if (this.viewData.generationPreviewId !== generationId) {
                return false;
            }

            const homePage = this.viewData.portfolioPages?.find(page => page.type === "home")
                || this.viewData.portfolioPages?.find(page => page.shortId === "home");
            if (!homePage) {
                return false;
            }

            this.applyViewData({
                ...this.viewData,
                project: homePage,
                activePage: homePage.shortId,
                generationPreviewId: generationId,
            });
            this.$store.state.project = homePage;
            this.error = false;
            this.password = false;
            this.comingSoon = false;
            this.loaded = true;
            store.commit("view/updateViewModeReadiness", true);
            return true;
        },
        setData(err, data, generationPreviewId = null) {
            if(err && !data) {
                if(err.response.status === 404) {
                    this.error = true;
                    this.loaded = true;
                    return;
                } else if (err.response.status === 401) {
                    this.comingSoon = true;
                    this.loaded = true;
                    return;
                }

                this.applyViewData(this.buildViewDataFromResponse(err.response?.data));
                this.password = true;
                this.loaded = true;
                return;
            }
            else {
                this.error = false;
                this.password = false;
            }

            this.applyViewData(this.buildViewDataFromResponse(data, generationPreviewId));
            this.loaded = true;
        },
        fetchUrl(to) {
            let url = '/data'
            let portfolioId = to.params.portfolioId;
            let shortId = to.params.shortId

            if (to.matched?.some(route => route.meta?.adminPreview) && shortId) {
                return '/api/admin/portfolio-preview/' + encodeURIComponent(shortId);
            }

            if (isOwnerPreview(to) && shortId) {
                return '/api/portfolio-preview/' + encodeURIComponent(shortId);
            }

            if (to.query?.managerPreview && shortId) {
                const token = to.query.previewToken
                    ? '?previewToken=' + encodeURIComponent(to.query.previewToken)
                    : '';
                return '/data/preview/' + shortId + token;
            }

            if(shortId) {
                url += '/shortid/' + shortId
            }
            else if(portfolioId) {
                url += "/portfolio/" + portfolioId
            }
            else {
                url += '/portfolio'
            }

            if(this.ownDomainPassword) {
                return '/data/ownDomain';
            }

            return url;
        },
        hydrateFromGeneratedPreviewCache(to) {
            if (!isGeneratedPortfolioTemplatePreview(to)) {
                return false;
            }

            const payload = readGeneratedPortfolioPreview(to.params.shortId);
            if (!payload) {
                return false;
            }

            try {
                this.setData(null, payload);
                return true;
            } catch (error) {
                return false;
            }
        },
        hydrateFromExistingViewData(to) {
            const storePortfolio = this.viewData.portfolio;
            const storePages = this.viewData.portfolioPages;
            const routeShortId = to.params.shortId;
            const routePortfolioId = to.params.portfolioId;

            const matchesCurrentPortfolio =
                (routeShortId && storePortfolio?.shortId === routeShortId) ||
                (routePortfolioId && storePortfolio?.id === routePortfolioId);

            if (!matchesCurrentPortfolio || !storePages?.length) {
                return false;
            }

            const currentPage = storePages.find(p => p.shortId === "home")

            if (!currentPage) {
                return false;
            }

            this.$store.commit('view/updateViewProject', currentPage);
            this.$store.commit('view/updateActivePage', currentPage.shortId);
            this.$store.commit("view/updateView", { generationPreviewId: null });
            this.$store.state.project = currentPage;
            this.error = false;
            this.password = false;
            this.comingSoon = false;
            this.loaded = true;
            store.commit("view/updateViewModeReadiness", true);
            return true;
        },
        isPreviewingPortfolio(to) {
            const editorPortfolio = this.$store.state.portfolio;
            const editorPages = this.$store.state.portfolioPages;
            const editorCaseStudies = this.$store.state.caseStudyPages;

            if (!editorPortfolio || !editorPages?.length) {
                return false;
            }

            const routeShortId = to.params.shortId;
            const routePortfolioId = to.params.portfolioId;

            const matchesShortId = routeShortId && editorPortfolio.shortId === routeShortId;
            const matchesPortfolioId = routePortfolioId && (editorPortfolio.id === routePortfolioId || editorPortfolio._id === routePortfolioId);

            if (!matchesShortId && !matchesPortfolioId) {
                return false;
            }

            const homePage = editorPages.find(page => page.shortId === "home") || editorPages[0];

            const currentProject = this.$store.state.project || homePage;

            if (!currentProject) {
                return false;
            }

            const sanitizedCaseStudies = (editorCaseStudies || []).filter(caseStudy => !caseStudy.deletedAt);
            const footerProjects = sanitizedCaseStudies.filter(project => !project.draft && project.shortId !== currentProject.shortId).slice(0, 4);

            const viewData = {
                portfolio: editorPortfolio,
                project: currentProject,
                user: this.$store.state.user,
                portfolioPages: editorPages.filter(page => !page.deletedAt),
                caseStudies: sanitizedCaseStudies,
                activePage: currentProject.shortId,
                footerProjects,
                hasDraftCasestudies: sanitizedCaseStudies.some(cs => cs.draft),
                loggedInUserId: this.$store.state.user?._id,
                emergencyModeSettings: this.$store.state.emergencyModeSettings,
                portfolioTemplate: editorPortfolio.templateSettings,
                generationPreviewId: null
            };

            this.applyViewData(viewData);

            this.error = false;
            this.password = false;
            this.comingSoon = false;
            this.loaded = true;
            store.commit("view/updateViewModeReadiness", true);

            return true;
        },
        buildViewDataFromResponse(data, generationPreviewId = null) {
            if(!this.viewData.fontsLoaded) {
                checkFonts(data.fonts);
                data.fontNames
                    .filter((fontName) => !data.fonts.map(x => x.name).includes(fontName))
                    .forEach((name) => GoogleFontsHelper.getFontURL(name));
                this.viewData.fontsLoaded = true;
            }

            if (data.loggedInUserId === data.project.ownerId && !isTemplatePreviewRoute(this.$route)) {
                UserActions.getUser()
                    .then(() => PortfolioActions.getPortfolios())
                    .then(() => store.commit("view/updateViewModeReadiness", true))
                    .catch(() => store.commit("view/updateViewModePreloadFailed", true));
            }

            this.$store.state.portfolio = data.portfolio;
            this.$store.state.project = data.project;

            if(!data.portfolio.templateSettings.theme.includes('profile_')) {
                try {
                    import(`../../../styles/_${data.portfolio.templateSettings.theme.replaceAll('_', '-').replace('profile_', '')}-base.scss`);
                } catch (e) {
                    console.log('failed to import scss')
                }
            }

            return {
                portfolio:data.portfolio,
                project:data.project,
                user:data.user,
                portfolioPages:data.portfolioPages,
                activePage:data.project.shortId,
                footerProjects:data.footerProjects,
                caseStudies:data.caseStudies,
                hasDraftCasestudies:data.hasDraftCasestudies,
                loggedInUserId:data.loggedInUserId,
                emergencyModeSettings:data.emergencyModeSettings,
                portfolioTemplate: data.portfolioTemplate,
                generationPreviewId
            };
        },
        applyViewData(viewData) {
            this.password = false;
            this.$store.commit('view/updateView', viewData);
            this.$store.commit('view/updateActivePage', viewData.activePage);
        }
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        currentComponent() {
            if (this.viewData.portfolio && !this.comingSoon && !this.password && (this.loaded || this.ownDomainLoaded)) {
                return 'PortfolioPage';
            } else if (this.comingSoon) {
                return 'ComingSoon';
            } else if (this.password) {
                return 'PasswordProtection';
            } else if (this.error) {
                return 'ErrorPage';
            }
            return null;
        },
        currentProps() {
            if (this.comingSoon) {
                return {
                    type: 'portfolio'
                }
            }
            if (this.password) {
                return {
                    setData: this.setData,
                    fetchUrl: this.fetchUrl,
                    type: 'portfolio'
                };
            } else if (this.error) {
                return {
                    error: {
                        code: 404,
                        title: 'This page doesn’t exist',
                        message: 'We can’t seem to find the page you are looking for'
                    }
                };
            }
            return {};
        }
    }
}
</script>
